import { useEffect, useState, useMemo } from "react";
import { Box, CircularProgress, Backdrop, Typography } from "@mui/material";

// Components
import FilterDrawer from "./components/FilterDrawer";
import ReportToolbar from "./components/ReportToolbar";
import ReportChart from "./components/ReportChart";
import ReportTable from "./components/ReportTable";

// Hooks and Utils
import { useMonthlyExtensionIEEFTIReport } from "./hooks/useMonthlyExtensionIEEFTIReport";
import { 
  processReportData, 
  getFilteredData, 
  getStackedChartData,
  getStackKeysForChart
} from "./utils/dataProcessing";

const MonthlyExtensionIEEFTIReport = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  const {
    // State
    rawData,
    reportData,
    columns,
    viewMode,
    filters,
    appliedFilters,
    currency,
    isLoading,
    hasNoData,
    selectedCustomerName,
    
    // Options
    newReasonCodeOptions,
    customerNameOptions,
    countryOptions,
    volumeOptions,
    monthOptions,
    yearOptions,
    
    // State setters
    setViewMode,
    setReportData,
    setColumns,
    setSelectedCustomerName,
    
    // Handlers
    handleFilterChange,
    applyFilters,
    clearFilters,
    areAllFiltersValid,
    isApplyButtonEnabled
  } = useMonthlyExtensionIEEFTIReport();

  // Re-process data when raw data is updated, applied filters change, volume type changes, or customer selection changes
  useEffect(() => {
    if (rawData.length > 0) {
      const { processedColumns, processedRows } = processReportData(
        rawData, 
        appliedFilters, 
        filters, 
        newReasonCodeOptions,
        selectedCustomerName
      );
      setColumns(processedColumns);
      setReportData(processedRows);
    }
  }, [rawData, appliedFilters, filters.volume, newReasonCodeOptions, selectedCustomerName, setColumns, setReportData]);

  // Enhanced apply filters to close drawer
  const handleApplyFilters = () => {
    const success = applyFilters();
    if (success) {
      setIsFilterOpen(false);
    }
    return success;
  };

  // Get processed data for current view - use useMemo to recalculate when dependencies change
  const { stackedChartData, stackKeysForChart } = useMemo(() => {
    const filteredData = getFilteredData(reportData, appliedFilters);
    const chartData = getStackedChartData(filteredData, columns, rawData, appliedFilters, filters, selectedCustomerName);
    const chartKeys = getStackKeysForChart(filteredData, rawData, appliedFilters, selectedCustomerName);
    
    return {
      stackedChartData: chartData,
      stackKeysForChart: chartKeys
    };
  }, [reportData, appliedFilters, filters.volume, columns, rawData, selectedCustomerName]);

  const filteredData = getFilteredData(reportData, appliedFilters);

  // Custom toolbar component for table view
  const TableToolbar = () => (
    <ReportToolbar
      viewMode={viewMode}
      setViewMode={setViewMode}
      filters={filters}
      appliedFilters={appliedFilters}
      handleFilterChange={handleFilterChange}
      onOpenFilter={() => setIsFilterOpen(true)}
      volumeOptions={volumeOptions}
      currency={currency}
      showGridToolbar={true}
      reportData={filteredData}
      columns={columns}
      selectedCustomerName={selectedCustomerName}
      setSelectedCustomerName={setSelectedCustomerName}
      customerNameOptions={customerNameOptions}
    />
  );

  return (
    <Box sx={{ 
      height: '100vh', 
      width: '100%', 
      backgroundColor: 'white',
      position: 'relative',
      // Global override for any gray backgrounds in the data grid
      '& *': {
        backgroundColor: 'white !important',
      },
      '& .MuiDataGrid-root': {
        border: 'none',
        backgroundColor: 'white !important',
        // Override any potential gray backgrounds
        '& *': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-main': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-virtualScroller': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-virtualScrollerContent': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-virtualScrollerRenderZone': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-cell': {
          borderBottom: 'none',
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-row': {
          backgroundColor: 'white !important',
          '&:hover': {
            backgroundColor: '#f5f5f5 !important',
          },
        },
        '& .MuiDataGrid-columnHeaders': {
          borderBottom: 'none',
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-columnHeadersInner': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-columnHeader': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-columnHeader--emptyGroup': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-columnHeader--withRightBorder': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-columnSeparator': {
          display: 'none',
        },
        '& .MuiDataGrid-scrollArea': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-scrollArea--left': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-scrollArea--right': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-filler': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-filler--header': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-container--top [data-field]': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGrid-container--top': {
          backgroundColor: 'white !important',
        },
        // Additional Pro-specific classes
        '& .MuiDataGridPro-root': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGridPro-main': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGridPro-columnHeaders': {
          backgroundColor: 'white !important',
        },
        '& .MuiDataGridPro-columnHeader': {
          backgroundColor: 'white !important',
        },
        // Override any default theme colors
        '& .MuiDataGrid-withBorderColor': {
          backgroundColor: 'white !important',
        },
        '& [class*="MuiDataGrid"]': {
          backgroundColor: 'white !important',
        },
      }
    }}>
      {/* Loading Backdrop */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: 'absolute',
          backgroundColor: 'rgba(255, 255, 255, 0.8)'
        }}
        open={isLoading}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      
      {/* Show "No data found" message when there's no data */}
      {hasNoData && !isLoading ? (
        <Box>
          {/* Toolbar for No Data View */}
          <ReportToolbar
            viewMode={viewMode}
            setViewMode={setViewMode}
            filters={filters}
            appliedFilters={appliedFilters}
            handleFilterChange={handleFilterChange}
            onOpenFilter={() => setIsFilterOpen(true)}
            volumeOptions={volumeOptions}
            currency={currency}
            showGridToolbar={false}
            selectedCustomerName={selectedCustomerName}
            setSelectedCustomerName={setSelectedCustomerName}
            customerNameOptions={customerNameOptions}
          />
          
          {/* No Data Message */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '50vh',
            textAlign: 'center' 
          }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No Data Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No extension request data available for the selected criteria.
            </Typography>
          </Box>
        </Box>
      ) : (
        <>
          {viewMode === 'table' ? (
            <ReportTable
              data={filteredData}
              columns={columns}
              loading={!reportData || reportData.length === 0}
              toolbar={TableToolbar}
            />
          ) : (
        <Box>
          {/* Toolbar for Chart View */}
          <ReportToolbar
            viewMode={viewMode}
            setViewMode={setViewMode}
            filters={filters}
            appliedFilters={appliedFilters}
            handleFilterChange={handleFilterChange}
            onOpenFilter={() => setIsFilterOpen(true)}
            volumeOptions={volumeOptions}
            currency={currency}
            showGridToolbar={false}
            selectedCustomerName={selectedCustomerName}
            setSelectedCustomerName={setSelectedCustomerName}
            customerNameOptions={customerNameOptions}
          />
          
          {/* Chart Container */}
          <ReportChart
            stackedChartData={stackedChartData}
            stackKeys={stackKeysForChart}
            appliedFilters={appliedFilters}
            filters={filters}
          />
        </Box>
      )}
      </>
      )}

      {/* Filter Drawer */}
      <FilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={filters}
        volumeOptions={volumeOptions}
        newReasonCodeOptions={newReasonCodeOptions}
        countryOptions={countryOptions}
        monthOptions={monthOptions}
        yearOptions={yearOptions}
        handleFilterChange={handleFilterChange}
        applyFilters={handleApplyFilters}
        clearFilters={clearFilters}
        areAllFiltersValid={areAllFiltersValid}
        isApplyButtonEnabled={isApplyButtonEnabled}
      />
    </Box>
  );
};

export default MonthlyExtensionIEEFTIReport;